// "Where do I stand at <School>?" on a college page (#3885; founder pick A of
// the college-page sign-up mocks, 2026-10-11: "Go with your recommendtion",
// https://claude.ai/artifact/Lx47y1w2eSegkWLQvDwibE). The words are the
// mock's. The read itself is Astra Lite's existing no-sign-in read; this file
// holds only the card's words and the pure helpers the card and its tests
// share (the next dated deadline, the residency sentence, the picture text).
import { US_STATE_NAMES } from '../utils/regions';

export const STAND = {
    title: (s) => `Where do I stand at ${s}?`,
    grade: 'Grade',
    gpa: 'Unweighted GPA',
    gpaPlaceholder: '3.7',
    home: 'Home state',
    homePlaceholder: 'Choose',
    go: 'Check it with my grades',
    checking: 'Checking...',
    gpaBad: 'Enter your unweighted GPA, from 0 to 4.',
    homeMissing: 'Choose your home state.',
    error: 'Something went wrong on our side. Please try again in a moment.',
    // The one action after the read. `short`: the next dated deadline ("Nov 1").
    // A school with no dated deadline ahead drops the date, never invents one.
    // No provider named (#5154): the sheet offers more than one way in.
    save: (short) => (short
        ? `Save this and remind me before ${short}`
        : 'Save this and remind me'),
    // 9th and 10th (founder, 2026-10-11: "yes 9th and 10th should be able to
    // use it"). Their GPA is a GPA so far and this cycle's deadlines are not
    // theirs, so the card says what the read is (Your Year's own line to
    // freshmen: "Colleges read four years of record") and the button promises
    // only what the carry does: the read kept, the school on their list
    // (concierge_carry.list_school). Never a reminder date.
    early: 'This reads your grades so far. Colleges read all four years of your record, '
        + 'so the years ahead count too.',
    saveList: (school) => `Save this and add ${school} to my list`,
};

// Ids are what the analytics event carries.
export const GRADES = [
    { id: 9, label: '9th', words: 'a freshman in 9th grade' },
    { id: 10, label: '10th', words: 'a sophomore in 10th grade' },
    { id: 11, label: '11th', words: 'a junior in 11th grade' },
    { id: 12, label: '12th', words: 'a senior in 12th grade' },
];
export const DEFAULT_GRADE = 12;

/** 9th or 10th: not applying this cycle, so no deadline and no reminder. */
export function isEarlyGrade(grade) {
    return grade === 9 || grade === 10;
}

/** The school's two-letter state from its "City, ST" location, or null. */
export function schoolState(c) {
    const m = /,\s*([A-Z]{2})\s*$/.exec(String((c && c.location) || ''));
    return m && US_STATE_NAMES[m[1]] ? m[1] : null;
}

/**
 * The home-state options: the school's own state first (as the mock shows),
 * then every other state by name. A real state, never "out of state": the
 * read picks the in-state or out-of-state rate from the visitor's state, and
 * with no state it falls back to the overall rate and says the home state
 * isn't on file, which would contradict the card.
 */
export function homeOptions(c) {
    const own = schoolState(c);
    const rest = Object.entries(US_STATE_NAMES)
        .filter(([code]) => code !== own)
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([code, name]) => ({ code, name }));
    return own ? [{ code: own, name: US_STATE_NAMES[own] }, ...rest] : rest;
}

/** A GPA the card accepts: unweighted, above 0 and at most 4. */
export function gpaOf(raw) {
    const s = String(raw ?? '').trim();
    if (!/^\d(?:\.\d{1,2})?$|^\.\d{1,2}$/.test(s)) return null;
    const v = Number(s);
    return v > 0 && v <= 4 ? v : null;
}

/**
 * What the picture is told, in a visitor's words: the free read parses grade,
 * GPA and location from it (the same parse the landing page's paragraph goes
 * through). Never anything the visitor did not choose.
 */
export function pictureText({ grade, gpa, home, schoolName }) {
    const g = GRADES.find((x) => x.id === grade) || GRADES.find((x) => x.id === DEFAULT_GRADE);
    return `I'm ${g.words} with a ${gpa} unweighted GPA, and I live in ${US_STATE_NAMES[home]}. `
        + `Where do I stand at ${schoolName}?`;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
    'September', 'October', 'November', 'December'];
const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Only a plain calendar date is a deadline to remind about: "November 1" or
// "November 1, 2026". "Rolling", "Preferred by April 1", "After February 1"
// and the like are not dated, so they never become a reminder date.
const DATE_RE = /^(January|February|March|April|May|June|July|August|September|October|November|December) (\d{1,2})(?:, (\d{4}))?$/;

/**
 * The date a deadline row means, as a local Date, or null. A row without a
 * year belongs to the cycle the table is for: entering Fall 2027 applies
 * August 2026 to July 2027, so August to December fall in 2026 and January
 * to July in 2027.
 */
export function deadlineDate(text, entering) {
    const m = DATE_RE.exec(String(text || '').trim());
    if (!m) return null;
    const month = MONTHS.indexOf(m[1]);
    let year = m[3] ? Number(m[3]) : null;
    if (!year) {
        const fall = Number((/(\d{4})/.exec(String(entering || '')) || [])[1]);
        if (!fall) return null;
        year = month >= 7 ? fall - 1 : fall;
    }
    const d = new Date(year, month, Number(m[2]));
    return d.getMonth() === month ? d : null;
}

/**
 * The next dated deadline on the page's own table, today included:
 * { plans: ['Early Action'], label: 'November 1', short: 'Nov 1' }, or null
 * when nothing dated is ahead. Plans that close the same day are named
 * together.
 */
export function nextDeadline(deadlines, today = new Date()) {
    if (!deadlines || !Array.isArray(deadlines.rows)) return null;
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const dated = deadlines.rows
        .map((r) => ({ r, d: deadlineDate(r.deadline, deadlines.entering) }))
        .filter((x) => x.d && x.d >= start);
    if (!dated.length) return null;
    const first = Math.min(...dated.map((x) => x.d.getTime()));
    const same = dated.filter((x) => x.d.getTime() === first);
    const d = same[0].d;
    return {
        plans: [...new Set(same.map((x) => x.r.plan))],
        label: `${MONTHS[d.getMonth()]} ${d.getDate()}`,
        short: `${SHORT[d.getMonth()]} ${d.getDate()}`,
    };
}

/** "Early Action closes", "Early Decision and Early Action close". */
export function closesLine(next) {
    const plans = next.plans;
    const who = plans.length > 1 ? `${plans.slice(0, -1).join(', ')} and ${plans[plans.length - 1]}` : plans[0];
    return { who, verb: plans.length > 1 ? 'close' : 'closes', when: next.label };
}

const rateOf = (r) => (r && Number.isFinite(r.applied) && Number.isFinite(r.admitted) && r.applied > 0
    ? Math.round((1000 * r.admitted) / r.applied) / 10 : null);

/**
 * The page's own residency figures, the visitor's group first: "Out-of-state
 * applicants were admitted at 43.6% for Fall 2025, in-state at 71.1%." Null
 * where the page has no in-state and out-of-state split.
 */
export function residencyLine(c, home) {
    const adm = (c && c.admission) || {};
    const rows = Array.isArray(adm.residency) ? adm.residency : [];
    const inRow = rows.find((r) => /^In-state\b/.test(String(r.group || '')));
    const outRow = rows.find((r) => r.group === 'Out-of-state');
    const inRate = rateOf(inRow);
    const outRate = rateOf(outRow);
    if (inRate === null || outRate === null) return null;
    const year = adm.residencyEntering || adm.entering;
    return home && home === schoolState(c)
        ? `In-state applicants were admitted at ${inRate}% for ${year}, out-of-state at ${outRate}%.`
        : `Out-of-state applicants were admitted at ${outRate}% for ${year}, in-state at ${inRate}%.`;
}
